import type { ReactNode } from "react";

import { Skeleton } from "@/shared/components/ui/skeleton";
import { cn } from "@/shared/lib/utils";

interface StatCardProps {
  /** Label corta arriba — kicker mono. */
  label: string;
  /** Métrica principal. Puede ser número o string ya formateado. */
  value: ReactNode;
  /** Hint pequeña bajo el valor (p.ej. "activos", "/ 120 total"). */
  hint?: ReactNode;
  /** Icono opcional en pill primary/10 arriba derecha. */
  icon?: ReactNode;
  /** Estado de carga — renderiza skeletons con el mismo footprint. */
  loading?: boolean;
  /** `delta` opcional — número o jsx — para mostrar cambio vs periodo previo. */
  trend?: {
    value: ReactNode;
    direction: "up" | "down" | "flat";
  };
  className?: string;
}

/**
 * Card de métrica para dashboards. Jerarquía:
 *   kicker (mono micro)
 *   value (display Fraunces, grande)
 *   hint (sans micro)
 *
 * No es una Card de shadcn — es un envoltorio propio para controlar el
 * ritmo tipográfico premium (más sutil que `<Card>`).
 */
export function StatCard({
  label,
  value,
  hint,
  icon,
  loading = false,
  trend,
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border bg-card p-5 shadow-card-premium transition-shadow",
        "hover:shadow-md",
        className,
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </p>
        {icon ? (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
            {icon}
          </div>
        ) : null}
      </div>

      {loading ? (
        <>
          <Skeleton className="h-9 w-24" />
          {hint ? <Skeleton className="mt-2 h-3 w-32" /> : null}
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <p className="font-display text-[2rem] font-medium tabular-nums leading-none tracking-display-tight text-foreground">
              {value}
            </p>
            {trend ? <TrendBadge {...trend} /> : null}
          </div>
          {hint ? (
            <p className="mt-2 text-[12.5px] leading-tight text-muted-foreground">{hint}</p>
          ) : null}
        </>
      )}
    </div>
  );
}

function TrendBadge({ value, direction }: { value: ReactNode; direction: "up" | "down" | "flat" }) {
  const tones = {
    up: "text-emerald-700 dark:text-emerald-300",
    down: "text-destructive",
    flat: "text-muted-foreground",
  };
  const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 font-mono text-[11px] font-medium",
        tones[direction],
      )}
    >
      <span aria-hidden>{arrow}</span>
      {value}
    </span>
  );
}
